import { useMemo, useState } from 'react';
import {
  CONTRACT_PROTECTIONS,
  EXTRACTION_SIGNALS,
  FLEX_LEVELS,
  FLEX_MATCH_OPTIONS,
  OWNERSHIP_TYPES,
  type DdwConfig,
} from '../config';
import {
  adoptionConfidence,
  fmtMoney,
  projectVendorTax,
  valueAtRisk,
  vendorSpend18mo,
} from '../calc';
import type {
  DdwLens,
  DdwScore,
  DdwVendor,
  FlexMatchKey,
  OwnershipKey,
  SpendTierKey,
} from '../types';

interface Props {
  vendor: DdwVendor;
  lens: DdwLens;
  config: DdwConfig;
  onSave(score: DdwScore, action: 'return' | 'addAnother'): void;
  onCancel(): void;
  onClear(vendorId: string): void;
}

function emptyDraft(config: DdwConfig): DdwScore {
  const adoption: Record<string, string | null> = {};
  for (const q of config.adoptionQuestions) adoption[q.key] = null;
  return {
    outcomeFit: [null, null, null],
    emotionalFit: null,
    ownership: null,
    extraction: [null, null, null],
    flexMatch: null,
    scaleOut: null,
    scaleUp: null,
    protections: [false, false, false, false],
    adoption,
    spendTier: null,
    gut: 5,
  };
}

function backfillDraft(score: DdwScore, config: DdwConfig): DdwScore {
  const next: DdwScore = JSON.parse(JSON.stringify(score));
  if (!next.adoption) next.adoption = {};
  for (const q of config.adoptionQuestions) {
    if (!(q.key in next.adoption)) next.adoption[q.key] = null;
  }
  if (next.scaleOut === undefined) next.scaleOut = null;
  if (next.scaleUp === undefined) next.scaleUp = null;
  if (next.emotionalFit === undefined) next.emotionalFit = null;
  if (next.spendTier === undefined) next.spendTier = null;
  return next;
}

/**
 * Phase 2 — single-vendor scorecard. Eight sections rendered top to
 * bottom. Live projection card embedded in Section 5; live adoption
 * confidence callout in Section 7. The save buttons stay disabled
 * until every required field is filled.
 *
 * Note on re-render strategy: the prototype called rerenderPreservingScroll()
 * after every input. React handles this naturally — state updates
 * don't reset scroll position, so we don't need any equivalent.
 */
export function Phase2Scorecard({ vendor, lens, config, onSave, onCancel, onClear }: Props) {
  const [draft, setDraft] = useState<DdwScore>(() =>
    vendor.score ? backfillDraft(vendor.score, config) : emptyDraft(config),
  );

  const update = (patch: Partial<DdwScore>) => setDraft((prev) => ({ ...prev, ...patch }));
  const setOutcome = (idx: 0 | 1 | 2, value: number) =>
    setDraft((prev) => {
      const out = [...prev.outcomeFit] as DdwScore['outcomeFit'];
      out[idx] = value;
      return { ...prev, outcomeFit: out };
    });
  const setExtraction = (idx: 0 | 1 | 2, value: boolean) =>
    setDraft((prev) => {
      const ext = [...prev.extraction] as DdwScore['extraction'];
      ext[idx] = value;
      return { ...prev, extraction: ext };
    });
  const setProtection = (idx: 0 | 1 | 2 | 3, value: boolean) =>
    setDraft((prev) => {
      const arr = [...prev.protections] as DdwScore['protections'];
      arr[idx] = value;
      return { ...prev, protections: arr };
    });
  const setAdoption = (key: string, optKey: string) =>
    setDraft((prev) => ({ ...prev, adoption: { ...prev.adoption, [key]: optKey } }));

  const valid = useMemo(() => isComplete(draft, config), [draft, config]);
  const owner = OWNERSHIP_TYPES.find((o) => o.key === draft.ownership);
  const lensFlex = FLEX_LEVELS.find((f) => f.key === lens.flexibility);

  const projectionForCard = projectVendorTax(lens, draft, config);
  const baseline = lens.tax.calculatedAnnual;
  const delta = projectionForCard.total - baseline;
  const conf = adoptionConfidence(draft, config.adoptionQuestions);
  const risk = valueAtRisk(lens, draft, config);
  const spend = vendorSpend18mo(lens, draft, config);

  const protectionsCount = draft.protections.filter(Boolean).length;
  const protTone = protectionsCount === 0 ? 'is-warn' : '';
  let protBody: React.ReactNode;
  if (protectionsCount === 0) {
    protBody = (
      <>
        <strong>0 confirmed → 4 negotiation asks.</strong> All four protections will appear in your
        report as items to push for. That's expected at early-stage evaluation.
      </>
    );
  } else if (protectionsCount === 4) {
    protBody = (
      <>
        <strong>All 4 confirmed.</strong> No outstanding negotiation asks for this vendor.
      </>
    );
  } else {
    const asks = 4 - protectionsCount;
    protBody = (
      <>
        <strong>
          {protectionsCount} confirmed · {asks} negotiation ask{asks === 1 ? '' : 's'}.
        </strong>{' '}
        The unchecked item{asks === 1 ? '' : 's'} will appear in your report as something to push
        for in negotiation.
      </>
    );
  }

  return (
    <div className="ddw-scorecard-wrap">
      <div className="ddw-scorecard-header">
        <div>
          <span className="ddw-step-tag">Phase 2 · Score this vendor</span>
          <h1 className="ddw-scorecard-title">
            {vendor.name}
            {vendor.category && <span className="ddw-scorecard-title-cat">{vendor.category}</span>}
          </h1>
        </div>
        <button type="button" className="ddw-tbtn" onClick={onCancel}>
          ← Back to vendors
        </button>
      </div>

      <div className="ddw-scorecard-card">
        {/* Section 1 — Outcome fit */}
        <div className="ddw-score-section">
          <div className="ddw-score-section-label">1 of 8 · Outcome fit</div>
          <div className="ddw-score-section-title">Score this vendor against your three outcomes.</div>
          <div className="ddw-score-section-hint">
            For each outcome, rate how well this vendor would deliver. 1 = doesn't address. 5 =
            exceeds expectations.
          </div>
          {lens.outcomes
            .map((o, i) => ({ o, i: i as 0 | 1 | 2 }))
            .filter(({ o }) => o && o.trim())
            .map(({ o, i }) => (
              <div className="ddw-outcome-score-row" key={i}>
                <div className="ddw-outcome-score-label">
                  {i + 1}. {o}
                </div>
                <SegFive value={draft.outcomeFit[i]} onChange={(n) => setOutcome(i, n)} />
                <div className="ddw-seg-meta">
                  <span>Doesn't address</span>
                  <span>Exceeds</span>
                </div>
              </div>
            ))}
        </div>

        {/* Section 2 — Emotional fit */}
        <div className="ddw-score-section">
          <div className="ddw-score-section-label">2 of 8 · Emotional & social fit</div>
          <div className="ddw-score-section-title">
            Will this vendor move your team toward how you want to feel?
          </div>
          <div className="ddw-score-section-hint">
            You picked: <strong>{lens.emotional.feel.join(', ') || '—'}</strong>. You want to stop:{' '}
            <strong>{lens.emotional.avoid.join(', ') || '—'}</strong>. Functional features can be
            perfect; if this vendor doesn't move you toward those feelings, adoption will fail.
          </div>
          <div style={{ maxWidth: 480 }}>
            <SegFive
              value={draft.emotionalFit}
              onChange={(n) => update({ emotionalFit: n })}
            />
            <div className="ddw-seg-meta">
              <span>Walks team away from</span>
              <span>Walks team toward</span>
            </div>
          </div>
        </div>

        {/* Section 3 — Vendor ownership */}
        <div className="ddw-score-section">
          <div className="ddw-score-section-label">3 of 8 · Vendor ownership</div>
          <div className="ddw-score-section-title">Who owns this vendor?</div>
          <div className="ddw-score-section-hint">
            If unsure, ask their sales rep — and note their answer (or non-answer). Don't guess.
          </div>
          <div className="ddw-owner-grid" role="radiogroup" aria-label="Vendor ownership">
            {OWNERSHIP_TYPES.map((o) => (
              <button
                key={o.key}
                type="button"
                role="radio"
                aria-checked={draft.ownership === o.key}
                className={`ddw-owner-card is-clickable${draft.ownership === o.key ? ' is-selected' : ''}`}
                onClick={() => update({ ownership: o.key as OwnershipKey })}
              >
                <div className="ddw-owner-card-type">{o.short}</div>
                <div className="ddw-owner-card-label">{o.label}</div>
                <div className="ddw-owner-card-incentive">{o.sub}</div>
              </button>
            ))}
          </div>
          {owner && (
            <div className="ddw-info-note">
              <strong>Vendors with this ownership profile typically:</strong> {owner.behaviors}
            </div>
          )}
        </div>

        {/* Section 4 — Extraction signals */}
        <div className="ddw-score-section">
          <div className="ddw-score-section-label">4 of 8 · Extraction signals</div>
          <div className="ddw-score-section-title">Three quick yes/no checks.</div>
          <div className="ddw-score-section-hint">
            Ask their references; check Crunchbase or news. These are the early indicators of the
            extraction playbook.
          </div>
          {EXTRACTION_SIGNALS.map((sig, i) => (
            <div className="ddw-yn-row" key={sig.key}>
              <div className="ddw-yn-label">{sig.label}</div>
              <div className="ddw-yn-control">
                <button
                  type="button"
                  className={`ddw-yn-btn${draft.extraction[i] === true ? ' is-yes' : ''}`}
                  onClick={() => setExtraction(i as 0 | 1 | 2, true)}
                  aria-pressed={draft.extraction[i] === true}
                >
                  Yes
                </button>
                <button
                  type="button"
                  className={`ddw-yn-btn${draft.extraction[i] === false ? ' is-no' : ''}`}
                  onClick={() => setExtraction(i as 0 | 1 | 2, false)}
                  aria-pressed={draft.extraction[i] === false}
                >
                  No
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Section 5 — Adaptation capacity */}
        <div className="ddw-score-section">
          <div className="ddw-score-section-label">5 of 8 · Adaptation capacity</div>
          <div className="ddw-score-section-title">
            Three dimensions of how this vendor handles change.
          </div>
          <div className="ddw-score-section-hint">
            Your lens calls for{' '}
            <strong>{lensFlex ? lensFlex.label.toLowerCase() : '—'}</strong> flexibility. Each
            dimension below feeds the projected adaptation tax shown after this section.
          </div>

          <div className="ddw-sub-q">
            <div className="ddw-sub-q-label">5a. Flex — who makes routine configuration changes?</div>
            <div className="ddw-btngroup ddw-btngroup--cols-2" role="radiogroup">
              {FLEX_MATCH_OPTIONS.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  role="radio"
                  aria-checked={draft.flexMatch === f.key}
                  className={`ddw-pill${draft.flexMatch === f.key ? ' is-selected' : ''}`}
                  onClick={() => update({ flexMatch: f.key as FlexMatchKey })}
                >
                  <span>{f.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="ddw-sub-q">
            <div className="ddw-sub-q-label">
              5b. Scale-OUT — adding new workgroups, user groups, use cases, or jurisdictions
            </div>
            <div className="ddw-btngroup" role="radiogroup">
              {config.scaleOut.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  role="radio"
                  aria-checked={draft.scaleOut === s.key}
                  className={`ddw-pill${draft.scaleOut === s.key ? ' is-selected' : ''}`}
                  onClick={() => update({ scaleOut: s.key })}
                >
                  <span>{s.label}</span>
                  <span className="ddw-pill-sub">{s.sub}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="ddw-sub-q">
            <div className="ddw-sub-q-label">
              5c. Scale-UP — more users, workflows, or volume in the existing context
            </div>
            <div className="ddw-btngroup" role="radiogroup">
              {config.scaleUp.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  role="radio"
                  aria-checked={draft.scaleUp === s.key}
                  className={`ddw-pill${draft.scaleUp === s.key ? ' is-selected' : ''}`}
                  onClick={() => update({ scaleUp: s.key })}
                >
                  <span>{s.label}</span>
                  <span className="ddw-pill-sub">{s.sub}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="ddw-projection-card" aria-live="polite">
            <div className="ddw-projection-card-title">
              Projected adaptation tax with this vendor
            </div>
            <div className="ddw-projection-card-total">
              {fmtMoney(projectionForCard.total)}
              <span className="ddw-projection-card-period">/yr</span>
            </div>
            <div className="ddw-projection-card-delta">
              vs baseline {fmtMoney(baseline)} →{' '}
              <strong style={{ color: delta > 0 ? '#fcd34d' : '#5eead4' }}>
                {delta >= 0 ? '+' : '−'}
                {fmtMoney(Math.abs(delta))}
              </strong>
            </div>
            <div className="ddw-projection-card-breakdown">
              <div>
                <span>Routine config</span>
                <strong>{fmtMoney(projectionForCard.routineCost)}</strong>
              </div>
              <div>
                <span>Scale-OUT penalty</span>
                <strong>{fmtMoney(projectionForCard.scaleOutPenalty)}</strong>
              </div>
              <div>
                <span>Scale-UP penalty</span>
                <strong>{fmtMoney(projectionForCard.scaleUpPenalty)}</strong>
              </div>
              <div>
                <span>Workarounds</span>
                <strong>{fmtMoney(projectionForCard.workaroundCost)}</strong>
              </div>
              <div>
                <span>Major events</span>
                <strong>{fmtMoney(projectionForCard.majorEventCost)}</strong>
              </div>
              <div>
                <span>Productivity drag</span>
                <strong>{fmtMoney(projectionForCard.dragCost)}</strong>
              </div>
            </div>
          </div>
        </div>

        {/* Section 6 — Contract protections */}
        <div className="ddw-score-section">
          <div className="ddw-score-section-label">6 of 8 · Contract protections</div>
          <div className="ddw-score-section-title">What has the vendor confirmed in writing?</div>
          <div className="ddw-score-section-hint">
            Tick only what the vendor has confirmed in writing — in their MSA, a redline, or a
            written commitment. If you haven't asked yet or only got verbal signals, leaving these
            unchecked is the honest answer. Unchecked items will appear in your report as{' '}
            <strong>negotiation asks</strong>.
          </div>
          <div className="ddw-checkgrid">
            {CONTRACT_PROTECTIONS.map((p, i) => (
              <label key={p.key} className="ddw-check">
                <input
                  type="checkbox"
                  checked={draft.protections[i]}
                  onChange={(e) => setProtection(i as 0 | 1 | 2 | 3, e.target.checked)}
                />
                <span>{p.label}</span>
              </label>
            ))}
          </div>
          <div className={`ddw-info-note ${protTone}`} style={{ marginTop: 14 }}>
            {protBody}
          </div>
        </div>

        {/* Section 7 — Adoption risk + spend tier */}
        <div className="ddw-score-section">
          <div className="ddw-score-section-label">7 of 8 · Adoption risk</div>
          <div className="ddw-score-section-title">Will this actually work in 12–18 months?</div>
          <div className="ddw-score-section-hint">
            Five quick checks predict whether you'll see real value — or be sitting on a stranded
            investment. Each maps to a known adoption-failure pattern.
          </div>

          {config.adoptionQuestions.map((q) => (
            <div className="ddw-sub-q" key={q.key}>
              <div className="ddw-sub-q-label">{q.label}</div>
              <div
                className="ddw-btngroup"
                style={{ gridTemplateColumns: `repeat(${q.options.length}, 1fr)` }}
                role="radiogroup"
              >
                {q.options.map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    role="radio"
                    aria-checked={draft.adoption[q.key] === opt.key}
                    className={`ddw-pill ddw-pill--small${
                      draft.adoption[q.key] === opt.key ? ' is-selected' : ''
                    }`}
                    onClick={() => setAdoption(q.key, opt.key)}
                  >
                    <span style={{ fontSize: 12 }}>{opt.label}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}

          <div
            className="ddw-sub-q"
            style={{
              marginTop: 18,
              paddingTop: 18,
              borderTop: '1px dashed var(--border)',
            }}
          >
            <div className="ddw-sub-q-label">Annual subscription tier (best estimate)</div>
            <p
              style={{
                fontSize: 12,
                color: 'var(--textMuted)',
                margin: '0 0 8px',
              }}
            >
              For the value-at-risk projection. A tier is fine — exact numbers come at quote time.
            </p>
            <div
              className="ddw-btngroup"
              style={{ gridTemplateColumns: `repeat(${config.spendTiers.length}, 1fr)` }}
              role="radiogroup"
            >
              {config.spendTiers.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  role="radio"
                  aria-checked={draft.spendTier === t.key}
                  className={`ddw-pill${draft.spendTier === t.key ? ' is-selected' : ''}`}
                  onClick={() => update({ spendTier: t.key as SpendTierKey })}
                >
                  <span style={{ fontSize: 12 }}>{t.label}</span>
                  <span className="ddw-pill-sub">{t.sub}</span>
                </button>
              ))}
            </div>
          </div>

          {draft.spendTier && (
            <div
              className={`ddw-info-note${conf < 70 ? ' is-warn' : ''}`}
              style={{ marginTop: 14 }}
              aria-live="polite"
            >
              <strong>Adoption confidence: {conf}%.</strong> At your projected 18-month spend of{' '}
              <strong>{fmtMoney(spend.total)}</strong> ({fmtMoney(spend.license18mo)} subscription +{' '}
              {fmtMoney(spend.internalAdaptation18mo)} internal adaptation), that's{' '}
              <strong style={{ color: 'var(--warning)' }}>{fmtMoney(risk)}</strong> at risk if this
              fails.
              {conf < 70 ? ' Recommended next step: run a premortem before signing.' : ''}
            </div>
          )}
        </div>

        {/* Section 8 — Gut */}
        <div className="ddw-score-section">
          <div className="ddw-score-section-label">8 of 8 · Gut confidence</div>
          <div className="ddw-score-section-title">
            How confident are you they'll deliver against your outcomes?
          </div>
          <div className="ddw-score-section-hint">
            Trust the gut. Years of experience compress into this number — don't second-guess it.
          </div>
          <div className="ddw-slider-block">
            <div className="ddw-slider-row">
              <input
                type="range"
                className="ddw-slider-input"
                min={1}
                max={10}
                value={draft.gut}
                onChange={(e) => update({ gut: parseInt(e.target.value, 10) })}
                aria-label="Gut confidence (1 to 10)"
                aria-valuemin={1}
                aria-valuemax={10}
                aria-valuenow={draft.gut}
              />
              <div className="ddw-slider-value">{draft.gut}</div>
            </div>
            <div className="ddw-slider-meta">
              <span>1 — Walk away</span>
              <span>10 — Bet the company</span>
            </div>
          </div>
        </div>
      </div>

      <div className="ddw-scorecard-footer">
        <div className="ddw-scorecard-footer-left">
          <button type="button" className="ddw-tbtn" onClick={onCancel}>
            Cancel
          </button>
          {vendor.scored && (
            <button
              type="button"
              className="ddw-tbtn is-danger"
              onClick={() => {
                if (confirm('Clear this score?')) onClear(vendor.id);
              }}
            >
              Clear score
            </button>
          )}
        </div>
        <div className="ddw-scorecard-footer-right">
          <span style={{ fontSize: 12, color: 'var(--textMuted)', marginRight: 8 }}>
            {valid ? '' : 'Fill all sections to save'}
          </span>
          <button
            type="button"
            className="ddw-tbtn"
            disabled={!valid}
            onClick={() => onSave(draft, 'addAnother')}
          >
            Save & add another
          </button>
          <button
            type="button"
            className="ddw-sbtn"
            disabled={!valid}
            onClick={() => onSave(draft, 'return')}
          >
            Save & return
          </button>
        </div>
      </div>
    </div>
  );
}

function SegFive({
  value,
  onChange,
}: {
  value: number | null;
  onChange(n: number): void;
}) {
  return (
    <div className="ddw-seg" role="radiogroup" aria-label="Score 1 through 5">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          role="radio"
          aria-checked={value === n}
          className={`ddw-seg-btn${value === n ? ' is-selected' : ''}`}
          onClick={() => onChange(n)}
        >
          {n}
        </button>
      ))}
    </div>
  );
}

function isComplete(d: DdwScore, config: DdwConfig): boolean {
  if (d.outcomeFit.some((x) => x == null)) return false;
  if (d.emotionalFit == null) return false;
  if (!d.ownership) return false;
  if (d.extraction.some((x) => x == null)) return false;
  if (!d.flexMatch) return false;
  if (!d.scaleOut) return false;
  if (!d.scaleUp) return false;
  if (!d.adoption) return false;
  for (const q of config.adoptionQuestions) {
    if (!d.adoption[q.key]) return false;
  }
  if (!d.spendTier) return false;
  if (d.gut == null) return false;
  return true;
}
